/* eslint-disable */
import PropTypes from 'prop-types';
import { forwardRef, useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'store';

import { Formik, useFormik } from 'formik';
import * as Yup from 'yup';

// material-ui
import { useTheme, styled } from '@mui/material/styles';
import {
    Button,
    FormHelperText,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    InputLabel,
    Slide,
    TextField,
    Typography,
    FormControl,
    Select,
    MenuItem,
    InputAdornment,
    List,
    ListItem,
    Input,
    Divider,
    Box
} from '@mui/material';

// project imports
import { gridSpacing, roles } from 'store/constant';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { openSnackbar } from 'store/slices/snackbar';
import { LoadingButton } from '@mui/lab';
import useAuth from 'hooks/useAuth';
import axiosServices from 'utils/axios';
import { EmployeeActions } from 'store/slices/employees';
import useDebounce from 'hooks/useDebounce';
import SearchIcon from '@mui/icons-material/Search';
import { findKeyInObject } from 'utils/findKeyInObjects';
import { ListingActions } from 'store/slices/listings';

// import { openSnackbar } from 'store/slices/snackbar';

// animation
const Transition = forwardRef((props, ref) => <Slide direction="left" ref={ref} {...props} />);

// ==============================|| PRODUCT ADD DIALOG ||============================== //

const ShareAccess = ({ openDialog, handleCloseDialog, listingId }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const { user, role } = useAuth();

    const [managerOptions, setManagerOptions] = useState([]);

    const { managers } = useSelector((state) => state.employee);
    const { getMyApartmentManagers, fetchEmployeeContacts } = EmployeeActions;
    const { shareListingAccess, fetchListingsAccessess } = ListingActions;

    // Update validation schema to handle an array of ids
    const validationSchema = Yup.object().shape({
        ids: Yup.array()
    });

    useEffect(() => {
        if (listingId) {
            dispatch(fetchListingsAccessess(listingId)).then((e) => {
                if (role === roles?.ADMIN) {
                    setFieldValue(
                        'ids',
                        e?.payload?.map((e) => e?.user_id)
                    );
                } else {
                    setFieldValue(
                        'ids',
                        e?.payload
                            ?.map((e) => e?.user_id)
                            ?.filter((userId) => {
                                return managers?.map((d) => d?.apartment_manager_id)?.some((id) => id === userId);
                            })
                    );
                }
            });
        }
    }, [listingId]);

    const onSubmit = async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
            if (!listingId) {
                throw new Error();
            }

            const response = await dispatch(shareListingAccess({ property_id: listingId, user_id: values.ids }));

            if (response?.payload?.status === 201 || (response?.payload?.status === 200 && response?.payload?.data?.success)) {
                dispatch(
                    openSnackbar({
                        open: true,
                        message: 'Property access updated successfully.',
                        variant: 'alert',
                        alert: {
                            color: 'success'
                        },
                        close: true
                    })
                );
                handleCloseClick();
                setStatus({ success: true });
                setSubmitting(false);
            } else {
                dispatch(
                    openSnackbar({
                        open: true,
                        message:
                            findKeyInObject(response?.payload, `message`) ||
                            findKeyInObject(response?.payload, `error`) ||
                            `Oops! Something went wrong.`,
                        variant: 'alert',
                        alert: {
                            color: 'error'
                        },
                        close: true
                    })
                );
            }
        } catch (err) {
            console.error(err);
            setStatus({ success: false });
            setErrors({ submit: 'Invalid credentials' });
            setSubmitting(false);
        }
    };

    const { values, handleSubmit, handleChange, handleBlur, errors, setFieldValue, touched, isSubmitting } = useFormik({
        onSubmit,
        validationSchema,
        initialValues: { ids: [] }, // Initialize with an empty array
        validateOnBlur: false,
        validateOnChange: false,
        enableReinitialize: true
    });

    const handleCloseClick = () => {
        handleCloseDialog();
    };

    useEffect(() => {
        if (role === roles.ADMIN) {
            dispatch(fetchEmployeeContacts({ search: '' })).then((e) => {
                setManagerOptions([...e?.payload?.primaryData, ...e?.payload?.secondaryData]);
            });
        }
        dispatch(getMyApartmentManagers({ page: 1, limit: 10000, search: '' }));
    }, []);

    return (
        <Dialog
            open={openDialog}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleCloseDialog}
            sx={{
                '&>div:nth-of-type(3)': {
                    justifyContent: 'flex-end',
                    '&>div': {
                        m: 0,
                        borderRadius: '0px',
                        maxWidth: 400,
                        maxHeight: '100%',
                        height: '100%'
                    }
                }
            }}
        >
            {openDialog && (
                <form onSubmit={handleSubmit} style={{ width: '400px' }}>
                    <>
                        <DialogTitle>Share Property Access</DialogTitle>
                        <DialogContent>
                            <Grid container spacing={gridSpacing} sx={{ mt: 0.25 }}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel id="ids">
                                            Select {(role === roles?.REGIONAL_MANAGER || role === roles?.MARKETING_MANAGER) && 'Apartment'}{' '}
                                            Manager
                                        </InputLabel>
                                        <Select
                                            id="ids"
                                            name="ids"
                                            multiple
                                            required
                                            value={values.ids}
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            label={`Select ${
                                                role === roles?.REGIONAL_MANAGER || role === roles?.MARKETING_MANAGER ? 'Apartment' : ''
                                            } Manager`}
                                            renderValue={(selected) =>
                                                selected?.length > 0 &&
                                                selected
                                                    .map((id) => {
                                                        if (role === roles?.ADMIN) {
                                                            const option = managerOptions.find((e) => e.id === id);
                                                            return `${option?.firstName} ${option?.lastName ?? ''}`;
                                                        } else {
                                                            const option = managers.find((e) => e?.apartmentManager?.id === id);
                                                            return `${option?.apartmentManager?.firstName} ${
                                                                option?.apartmentManager?.lastName ?? ''
                                                            }`;
                                                        }
                                                    })
                                                    .join(', ')
                                            }
                                        >
                                            {role === roles.ADMIN
                                                ? managerOptions
                                                      ?.filter((e) => e?.status === 'VERIFIED')
                                                      ?.map((e) => (
                                                          <MenuItem key={e.id} value={e.id} sx={{ display: 'block' }}>
                                                              {e?.firstName && (
                                                                  <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                                                                      {e?.firstName + ' ' + (e?.lastName ?? '') + ' (' + e?.roleName + ')'}
                                                                  </Typography>
                                                              )}
                                                              <Typography variant="body1">({e?.email})</Typography>
                                                          </MenuItem>
                                                      ))
                                                : managers
                                                      ?.filter((e) => e?.apartmentManager?.status === 'VERIFIED')
                                                      ?.map((e) => (
                                                          <MenuItem
                                                              key={e.apartmentManager?.id}
                                                              value={e.apartmentManager?.id}
                                                              sx={{ display: 'block' }}
                                                          >
                                                              {e?.apartmentManager?.firstName && (
                                                                  <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                                                                      {e?.apartmentManager?.firstName +
                                                                          ' ' +
                                                                          e?.apartmentManager?.lastName +
                                                                          ' '}
                                                                  </Typography>
                                                              )}
                                                              <Typography variant="body1">({e?.apartmentManager?.email})</Typography>
                                                          </MenuItem>
                                                      ))}
                                        </Select>
                                        {touched.ids && errors.ids && <FormHelperText error>{errors.ids}</FormHelperText>}
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Box sx={{ display: 'flex', flexDirection: 'row-reverse', justifyContent: 'space-between', width: '100%' }}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <AnimateButton>
                                        <LoadingButton
                                            onClick={handleSubmit}
                                            variant="contained"
                                            sx={{
                                                bgcolor: theme.palette.secondary.main,
                                                '&:hover': { bgcolor: theme.palette.secondary.dark }
                                            }}
                                            loading={isSubmitting}
                                        >
                                            {'Share'}
                                        </LoadingButton>
                                    </AnimateButton>
                                    <Button variant="text" color="error" onClick={handleCloseClick}>
                                        Close
                                    </Button>
                                </Box>
                            </Box>
                        </DialogActions>
                    </>
                </form>
            )}
        </Dialog>
    );
};

ShareAccess.propTypes = {
    openDialog: PropTypes.bool,
    handleCloseDialog: PropTypes.func
};

export default ShareAccess;
